import { type ReactNode } from 'react';
import { Link } from 'react-router';
import ClipLoader from 'react-spinners/ClipLoader';
import { CardWrapper } from '~/components/cards/CardWrapper';

export function DashboardColumnSpinner() {
  return (
    <div
      className="flex justify-center items-center py-12"
      role="status"
      aria-label="Loading"
    >
      <ClipLoader color="var(--color-prim)" size={24} />
    </div>
  );
}

export function DashboardColumn({
  title,
  subtitle,
  badge,
  footerLink,
  footerLabel,
  children,
}: {
  title: string;
  subtitle: string;
  badge?: number;
  footerLink?: string;
  footerLabel?: string;
  children: ReactNode;
}) {
  return (
    <CardWrapper padding="none">
      <div className="p-6 pb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-neut-prim">{title}</h2>
          {badge !== undefined && badge > 0 && (
            <span className="bg-prim-100 text-prim-700 text-xs font-medium rounded-full px-2 py-1">
              {badge}
            </span>
          )}
        </div>
        <p className="text-xs font-medium text-neut-sec mt-1">{subtitle}</p>
      </div>
      <div>{children}</div>
      {footerLink && footerLabel && (
        <div className="px-6 py-4 border-t border-neut-border">
          <Link
            to={footerLink}
            className="text-sm font-medium text-prim hover:text-prim-hover"
          >
            {footerLabel}
          </Link>
        </div>
      )}
    </CardWrapper>
  );
}

export interface DashboardListColumnProps<T> {
  title: string;
  subtitle: string;
  items: T[];
  isLoading: boolean;
  isError: boolean;
  emptyText: string;
  errorText: string;
  getKey: (item: T) => string;
  renderRow: (item: T) => ReactNode;
  sortBy?: (a: T, b: T) => number;
  limit?: number;
  badge?: number;
  footerLink?: string;
  footerLabel?: string;
}

/**
 * The loading/error/empty/sorted-and-capped-list shape shared by every dashboard column that
 * lists a handful of records (orders, requests for quote, quotes). `renderRow` supplies only the
 * row's own content — the surrounding card, spinner, and empty/error states are handled here.
 */
export function DashboardListColumn<T>({
  title,
  subtitle,
  items,
  isLoading,
  isError,
  emptyText,
  errorText,
  getKey,
  renderRow,
  sortBy,
  limit = 5,
  badge = items.length,
  footerLink,
  footerLabel,
}: Readonly<DashboardListColumnProps<T>>) {
  const sorted = sortBy ? [...items].sort(sortBy) : items;
  const visibleItems = sorted.slice(0, limit);
  // Without a footerLink there is no way to reach items past the limit, so the badge
  // must not promise a count the buyer can't otherwise see.
  const effectiveBadge = footerLink
    ? badge
    : Math.min(badge, visibleItems.length);

  let body: ReactNode;
  if (isLoading) {
    body = <DashboardColumnSpinner />;
  } else if (isError) {
    body = (
      <p className="text-sm font-medium text-neut-sec text-center px-6 py-8">
        {errorText}
      </p>
    );
  } else if (visibleItems.length === 0) {
    body = (
      <p className="text-sm font-medium text-neut-sec px-6 py-8">{emptyText}</p>
    );
  } else {
    body = visibleItems.map((item) => (
      <div
        key={getKey(item)}
        className="flex flex-col gap-1 px-6 py-4 border-b border-neut-border last:border-b-0"
      >
        {renderRow(item)}
      </div>
    ));
  }

  return (
    <DashboardColumn
      title={title}
      subtitle={subtitle}
      badge={effectiveBadge}
      footerLink={footerLink}
      footerLabel={footerLabel}
    >
      {body}
    </DashboardColumn>
  );
}
